import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/free-solid-svg-icons';

const traditions = [
  {
    title: 'La Galette',
    description:
      "Une pâte feuilletée dorée garnie de frangipane, un mélange onctueux d'amandes, de beurre et d'œufs. Chaque bouchée est un voyage en France.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
      </svg>
    ),
  },
  {
    title: 'La Fève',
    description:
      "Cachée dans la galette, cette petite figurine en porcelaine désigne le roi ou la reine du jour. Autrefois une vraie fève, aujourd'hui souvent un personnage de collection.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
      </svg>
    ),
  },
  {
    title: 'Le Rituel',
    description:
      "Le plus jeune se glisse sous la table et désigne à qui revient chaque part. Un moment de suspense et de rires garanti pour toute la famille !",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'Le Couronnement',
    description:
      "Celui qui trouve la fève est couronné et choisit son roi ou sa reine. Une tradition qui fait briller les yeux des petits... et des grands.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
];

export default function TraditionSection() {
  return (
    <section id="tradition" className="py-24 lg:py-32 bg-navy-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 text-gold/5 text-[12rem] hidden lg:block">
          <FontAwesomeIcon icon={faCrown} />
        </div>
        <div className="absolute bottom-20 right-10 text-gold/5 text-[8rem] transform rotate-12 hidden lg:block">
          <FontAwesomeIcon icon={faCrown} />
        </div>
      </div>

      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/95 to-navy-900 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <span className="inline-block font-body text-sm text-gold uppercase tracking-[0.2em] mb-4">
            Depuis le XIVe siècle
          </span>
          <h2 className="font-serif font-semibold text-4xl md:text-5xl text-cream mb-6 leading-tight">
            La Tradition de
            <span className="block text-gradient-gold">l'Épiphanie</span>
          </h2>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold/50" />
            <div className="crown-icon text-xl">
              <FontAwesomeIcon icon={faCrown} />
            </div>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-gold/50" />
          </div>
          <p className="font-body text-lg text-cream/60 leading-relaxed">
            Chaque année en janvier, les Français perpétuent une tradition gourmande
            qui célèbre l'Épiphanie. Un moment de partage où chacun espère
            secrètement devenir roi ou reine d'un jour.
          </p>
        </div>

        {/* Tradition cards - 2x2 grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {traditions.map((item, index) => (
            <article
              key={index}
              className="group relative flex gap-6 p-8 lg:p-10 bg-navy-800/50 border border-gold/10 backdrop-blur-sm transition-all duration-500 hover:bg-navy-800/80 hover:border-gold/30"
            >
              {/* Icon container */}
              <div className="flex-shrink-0">
                <div className="w-14 h-14 flex items-center justify-center bg-gold/10 border border-gold/20 text-gold transition-all duration-300 group-hover:bg-gold group-hover:text-navy-900">
                  {item.icon}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="font-serif text-xl font-semibold text-cream mb-3 group-hover:text-gold transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="font-body text-cream/60 leading-relaxed">
                  {item.description}
                </p>
              </div>

              {/* Hover accent line */}
              <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </article>
          ))}
        </div>

        {/* Quote */}
        <div className="mt-16 text-center">
          <blockquote className="relative inline-block max-w-2xl">
            <span className="absolute -top-6 -left-4 text-6xl text-gold/20 font-serif">"</span>
            <p className="font-serif text-xl italic text-cream/70 leading-relaxed">
              Tirer les rois — une expression française qui signifie partager la galette
              et désigner le roi du jour.
            </p>
            <span className="absolute -bottom-6 -right-4 text-6xl text-gold/20 font-serif rotate-180">"</span>
          </blockquote>
        </div>
      </div>
    </section>
  );
}
